// /src/native/auto-dim.ts
import { registerPlugin, Capacitor } from '@capacitor/core';

console.log('[Trauvo][auto-dim] module loaded');

export type AutoDimApi = {
  configure(opts: { idleSeconds?: number; dimLevel?: number }): Promise<void>;
  stop(): Promise<void>;
  ping(): Promise<void>;
};

// IMPORTANT: The name here MUST match the @objc(<Name>) in Swift.
// Swift file has: @objc(AutoDim) public class AutoDim: CAPPlugin { ... }
export const AutoDim = registerPlugin<AutoDimApi>('AutoDim', {
  // Web fallback so browser mode won't crash
  web: () =>
    ({
      async configure() {},
      async stop() {},
      async ping() {},
    } as AutoDimApi),
});

/**
 * Wire auto-dimming:
 * - configure native with idleSeconds + dimLevel
 * - re-arm timer on any user activity via ping()
 * This is a NO-OP on web (non-native) builds.
 */
export function wireAutoDim(
  { idleSeconds = 30, dimLevel = 0.5 }: { idleSeconds?: number; dimLevel?: number } = {}
) {
  // Only run on native (Capacitor WebView)
  try {
    if (!(Capacitor as any)?.isNativePlatform || Capacitor.isNativePlatform() !== true) {
      console.log('[Trauvo][auto-dim] skipped (not native)');
      return;
    }
  } catch {
    // If Capacitor is missing, bail gracefully
    return;
  }

  AutoDim.configure({ idleSeconds, dimLevel }).catch((e) => {
    console.warn('[Trauvo][auto-dim] configure failed', e);
  });

  const reset = () => AutoDim.ping().catch(() => {});
  const opts: AddEventListenerOptions = { passive: true };

  // Window-level events
  ['touchstart', 'touchmove', 'mousedown', 'mousemove', 'keydown', 'scroll', 'pointerdown', 'pointermove', 'wheel'].forEach(
    (evt) => window.addEventListener(evt as any, reset, opts)
  );

  // Document visibility: when returning to visible, reset timer
  document.addEventListener(
    'visibilitychange',
    () => {
      if (document.visibilityState === 'visible') reset();
    },
    opts
  );

  console.log('[Trauvo][auto-dim] wired (idleSeconds=%s, dimLevel=%s)', idleSeconds, dimLevel);
}

// --- Auto-wire once on native, even if main.ts forgets ---
let __wired = false;
export function ensureAutoDim(params?: { idleSeconds?: number; dimLevel?: number }) {
  if (__wired) return;
  __wired = true;
  wireAutoDim(params);
}

try {
  if ((Capacitor as any)?.isNativePlatform && Capacitor.isNativePlatform() === true) {
    const k = '__TRAUVO_AUTODIM_WIRED__';
    if (!(globalThis as any)[k]) {
      (globalThis as any)[k] = true;
      ensureAutoDim(); // default 30s / 0.5
      console.log('[Trauvo][auto-dim] auto-wired on native');
    }
  }
} catch { /* ignore */ }

// --- Make it easy to test from Safari Web Inspector on device. ---
try {
  (globalThis as any).AutoDim = AutoDim;
  (globalThis as any).AUTO_DIM_CONFIGURE = (s = 30, l = 0.5) => AutoDim.configure({ idleSeconds: s, dimLevel: l });
  (globalThis as any).AUTO_DIM_PING = () => AutoDim.ping();
  console.log('[Trauvo][auto-dim] globals attached →', {
    AutoDim: typeof (globalThis as any).AutoDim,
    AUTO_DIM_CONFIGURE: typeof (globalThis as any).AUTO_DIM_CONFIGURE,
    AUTO_DIM_PING: typeof (globalThis as any).AUTO_DIM_PING,
  });
} catch (e) {
  console.warn('[Trauvo][auto-dim] failed to expose globals', e);
}
